import { Component, HostListener, OnInit } from '@angular/core';
import { StepModel } from 'src/app/Models/step-model';
import { CTVModel } from 'src/app/Models/ctv-model';
import { webObjectToCandidates } from 'src/app/Models/web-object-candidates';
import { StepServiceService } from 'src/app/Services/step-service.service';
import { CTVService } from 'src/app/Services/ctv.service';
import { NamePromptService } from 'src/app/Services/name-prompt.service';
import { ElementDetailsService } from 'src/app/Services/element-details.service';
import { StepSaveService } from 'src/app/Services/step-save.service';

// "Browse what's saved" view, shown while no session is active. Same row/
// menu style as the active-session step list — deliberately not
// ze-object-list-main/ze-step-view (not read-only outside /canvas).
@Component({
  selector: 'ze-web-recorder-saved-steps',
  templateUrl: './web-recorder-saved-steps.component.html',
  styleUrls: ['./web-recorder-saved-steps.component.scss']
})
export class WebRecorderSavedStepsComponent implements OnInit {

  steps: StepModel[] = [];
  selectedStepId = '';
  isLoading = false;
  loadError = '';
  isPromoting = false;

  openMenuStepId: string | null = null;

  selectedStepCommands: CTVModel[] = [];
  isLoadingCommands = false;

  // Mirrors CodeTestIDE extension's own "English Description" toggle
  // (#plain-english-toggle in its command grid) — default on, same as there.
  showEnglishDescription = true;

  toastType: 'success' | 'error' = 'success';
  toastMessage: string | null = null;
  private toastTimer: any = null;

  constructor(
    private stepService: StepServiceService,
    private ctvService: CTVService,
    private namePrompt: NamePromptService,
    private elementDetails: ElementDetailsService,
    private stepSave: StepSaveService
  ) { }

  // Same "Element Details" popup, rebuilt from the backend-resolved target.
  onOpenElementDetails(c: CTVModel, event: MouseEvent): void {
    event.stopPropagation();
    this.elementDetails.open(webObjectToCandidates(c.target));
  }

  ngOnInit(): void {
    this.loadSteps();
  }

  loadSteps(): void {
    this.isLoading = true;
    this.loadError = '';
    this.stepService.getItemsdb2().subscribe({
      next: (res: any) => {
        // Only steps the Web Recorder itself saved (its "Save Steps"
        // checkbox) — NOT every step ever saved to Canvas by any means.
        this.steps = (res.data || []).filter((m: any) => !!m && m.source === 'web_recorder');
        this.isLoading = false;
      },
      error: () => {
        this.loadError = 'Could not load saved steps.';
        this.isLoading = false;
      }
    });
  }

  selectStep(model: StepModel): void {
    this.selectedStepId = model['_id'];
    this.selectedStepCommands = [];
    this.isLoadingCommands = true;
    this.ctvService.getItemsByStepdb2(this.selectedStepId).subscribe({
      next: (res: any) => {
        this.selectedStepCommands = ((res && res.data) || []).filter(Boolean);
        this.isLoadingCommands = false;
      },
      error: () => {
        this.isLoadingCommands = false;
      }
    });
  }

  isMenuOpen(step: StepModel): boolean {
    return this.openMenuStepId === step['_id'];
  }

  toggleMenu(step: StepModel, event: MouseEvent): void {
    event.stopPropagation();
    this.openMenuStepId = this.isMenuOpen(step) ? null : step['_id'];
  }

  @HostListener('document:click')
  closeMenu(): void {
    this.openMenuStepId = null;
  }

  onMenuDelete(step: StepModel): void {
    this.openMenuStepId = null;
    const stepId = step['_id'];
    this.stepService.deleteStepdb2(stepId).subscribe({
      next: () => {
        this.steps = this.steps.filter(s => s['_id'] !== stepId);
        if (this.selectedStepId === stepId) {
          this.selectedStepId = '';
          this.selectedStepCommands = [];
        }
        this.showToast('success', `"${step.name}" deleted.`);
      },
      error: () => this.showToast('error', `Failed to delete "${step.name}". Please try again.`)
    });
  }

  onMenuCopy(step: StepModel): void {
    this.openMenuStepId = null;
    this.stepService.copyStepdb2(step['_id'], step.name + ' (copy)').subscribe({
      next: () => {
        this.showToast('success', `"${step.name}" copied.`);
        this.loadSteps();
      },
      error: () => this.showToast('error', `Failed to copy "${step.name}". Please try again.`)
    });
  }

  async onMenuRename(step: StepModel): Promise<void> {
    this.openMenuStepId = null;
    const name = await this.namePrompt.open('Rename Step', step.name);
    if (!name) return;
    this.stepService.renameStepdb2(step['_id'], name).subscribe({
      next: () => { step.name = name; },
      error: () => this.showToast('error', `Failed to rename "${step.name}". Please try again.`)
    });
  }

  // Promotes to Canvas (clears the tag). excludeStepId in the check avoids
  // colliding with itself.
  onSaveToCanvas(): void {
    if (!this.selectedStepId || this.isPromoting) return;
    const stepId = this.selectedStepId;
    const step = this.steps.find(s => s['_id'] === stepId);
    const stepName = (step && step.name || '').trim();
    const projectId = localStorage.getItem('projectIdDb2');
    if (!projectId) {
      this.showToast('error', 'No project selected. Please open a project first.');
      return;
    }

    this.isPromoting = true;
    this.stepSave.checkNameAvailable(projectId, stepName, stepId).subscribe(available => {
      if (!available) {
        this.isPromoting = false;
        this.showToast('error', `A step named "${stepName}" already exists. Please rename this step first.`);
        return;
      }
      this.stepService.promoteToCanvasdb2(stepId).subscribe({
        next: (res: any) => {
          this.isPromoting = false;
          // Returns HTTP 200 even when blocked server-side — check the body.
          if (res && res.operationStatus === 'ERROR') {
            this.showToast('error', res.operationMessage || 'Failed to save step to Canvas. Please try again.');
            return;
          }
          this.showToast('success', 'Step saved to Canvas.');
          this.steps = this.steps.filter(s => s['_id'] !== stepId);
          this.selectedStepId = '';
          this.selectedStepCommands = [];
        },
        error: () => {
          this.isPromoting = false;
          this.showToast('error', 'Failed to save step to Canvas. Please try again.');
        }
      });
    });
  }

  private showToast(type: 'success' | 'error', message: string): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastType = type;
    this.toastMessage = message;
    this.toastTimer = setTimeout(() => { this.toastMessage = null; this.toastTimer = null; }, 3500);
  }
}
